import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { FaPhoneAlt, FaWhatsapp, FaEnvelope, FaFilter, FaMapMarkerAlt, FaGlobeAmericas, FaGlobe, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { getLocationsByType } from '../services/distributorService';
import { formatUrlSlug } from '../utils/distributorData';

// Lazy load map components to improve initial load performance
const MexicoMap = lazy(() => import('../components/MexicoMap'));
const WorldMap = lazy(() => import('../components/WorldMap'));

const Distribuidores = () => {
  const [filter, setFilter] = useState('mexico'); // Cambiado a 'mexico' por defecto
  const [mapView, setMapView] = useState('mexico'); // 'mexico' o 'international'
  const [selectedDistributors, setSelectedDistributors] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showDistributorDetails, setShowDistributorDetails] = useState(false);
  const navigate = useNavigate();
  
  // Función para manejar clic en un estado del mapa
  const handleStateClick = (distributors, locationName) => {
    console.log(`Mostrando distribuidores para: ${locationName}`, distributors);
    setSelectedDistributors(distributors);
    setSelectedLocation(locationName);
    setShowDistributorDetails(true);
  };

  // Cambiar vista de mapa entre México e Internacional
  const toggleMapView = (view) => {
    setMapView(view);
    setFilter(view); // Sincronizar el filtro con la vista de mapa
    // Ocultar detalles de distribuidor al cambiar de mapa
    setShowDistributorDetails(false);
  };
  
  return (
    <>
      <SEO 
        title="Comprar OPUS | Distribuidores Autorizados | ECOSOFT"
        description="Adquiere OPUS a través de nuestro equipo de ventas directo o mediante nuestra red de distribuidores autorizados en México y el extranjero."
        keywords="OPUS, comprar, distribuidores, ventas, ECOSOFT, precio, cotización"
      />
      
      {/* Hero Section */}
      <section id="hero" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl font-bold mb-4">Comprar OPUS</h1>
            <p className="text-xl mb-8">
              Elige la forma más conveniente para adquirir nuestras soluciones a través de nuestro equipo de ventas o mediante nuestra red de distribuidores autorizados.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contacto" 
                className="px-8 py-3 bg-white text-[#0066CC] hover:bg-gray-100 font-medium rounded-md"
              >
                Contactar a ventas
              </Link>
              <a 
                href="tel:+525539908312" 
                className="px-8 py-3 border border-white hover:bg-white/10 text-white font-medium rounded-md transition-colors flex items-center"
              >
                <FaPhoneAlt className="mr-2" /> (55) 3990.8312
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Compra Directa Destacada */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4 text-[#1A2B39]">Contacta a ECOSOFT</h2>
                <p className="text-gray-600 mb-6">
                  La manera más rápida y directa de adquirir OPUS y nuestras soluciones es a través de nuestro equipo de ventas, que te ayudará a encontrar la mejor opción para tus necesidades.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4 mt-1">
                      <FaWhatsapp className="text-[#0055B8]" />
                    </div>
                    <div>
                      <p className="font-bold">WhatsApp Ventas</p>
                      <p className="text-[#0055B8] font-medium">(55) 3990.8312</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4 mt-1">
                      <FaPhoneAlt className="text-[#0055B8]" />
                    </div>
                    <div>
                      <p className="font-bold">Teléfono</p>
                      <p className="text-[#0055B8] font-medium">55 55 6363 55</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4 mt-1">
                      <FaEnvelope className="text-[#0055B8]" />
                    </div>
                    <div>
                      <p className="font-bold">Correo Electrónico</p>
                      <p className="text-[#0055B8] font-medium">ventas@ecosoft.com.mx</p>
                    </div>
                  </div>
                </div>
                
             
              </div>
              
              <div className="bg-[#F5F7FA] p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <img 
                    src="/images/Logo.png" 
                    alt="Ventas ECOSOFT" 
                    className="h-16 w-auto mr-4"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-[#1A2B39]">ECOSOFT</h3>
                    <p className="text-gray-600">Atención directa y personalizada</p>
                  </div>
                </div>
                <img 
                  src="/img/ventas-directas-ecosoft.jpg" 
                  alt="Equipo de Ventas ECOSOFT" 
                  className="rounded-lg w-full mb-4"
                />
                <p className="text-gray-700 mb-4">
                  Nuestro equipo de ventas está capacitado para asesorarte sobre la mejor solución para tu empresa o proyecto.
                </p>
                <div className="flex justify-end">
                  <a 
                    href="https://wa.me/525539908312" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#25D366] text-white rounded-md hover:bg-[#128C7E] transition-colors"
                  >
                    <FaWhatsapp className="mr-2" /> Contactar por WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Distribuidores Section */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-[#1A2B39]">Red de Distribuidores Autorizados</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            También puedes adquirir nuestros productos a través de nuestra red de distribuidores autorizados
          </p>
          
          {/* Mapa de distribuidores */}
          <div className="mb-10">
            <div className="flex justify-center mb-4">
              <div className="inline-flex bg-white rounded-md shadow-sm p-1">
                <button
                  onClick={() => toggleMapView('mexico')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                    mapView === 'mexico' 
                      ? 'bg-[#0055B8] text-white' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaMapMarkerAlt className="mr-2" /> México
                </button>
                <button
                  onClick={() => toggleMapView('international')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                    mapView === 'international' 
                      ? 'bg-[#0055B8] text-white' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaGlobeAmericas className="mr-2" /> Internacional
                </button>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Suspense fallback={
                <div className="flex justify-center items-center h-64">
                  <div className="inline-block w-8 h-8 border-4 border-[#0055B8] border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Cargando mapa...</span>
                </div>
              }>
                {mapView === 'mexico' ? (
                  <MexicoMap 
                    key={`mexico-map-${new Date().getTime()}`} 
                    onStateClick={handleStateClick}
                  />
                ) : (
                  <WorldMap 
                    key={`world-map-${new Date().getTime()}`}
                    onStateClick={handleStateClick}
                  />
                )}
              </Suspense>
              
              <div className="mt-4 text-sm text-gray-600 text-center">
                <p>Haz clic en un estado o país para ver los distribuidores disponibles</p>
              </div>
            </div>
          </div>
          
          {/* Sección de detalles del distribuidor */}
          {showDistributorDetails && (
            <div id="distributor-details" className="mt-12 mb-12 bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-[#1A2B39]">
                  Distribuidores en {selectedLocation}
                </h3>
                <button 
                  onClick={() => setShowDistributorDetails(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaChevronUp className="text-xl" />
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {selectedDistributors.map((distributor, index) => (
                  <div key={index} className="bg-[#F5F7FA] p-6 rounded-lg">
                    <h4 className="text-xl font-semibold mb-4 text-[#1A2B39]">{distributor.Name}</h4>
                    
                    <div className="space-y-3">
                      {distributor.Address && (
                        <div className="flex items-start">
                          <FaMapMarkerAlt className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                          <p>{distributor.Address}</p>
                        </div>
                      )}
                      
                      {distributor.Phone && (
                        <div className="flex items-start">
                          <FaPhoneAlt className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                          <a href={`tel:${distributor.Phone.replace(/[^\d+]/g, '')}`} className="hover:text-[#0055B8]">
                            {distributor.Phone}
                          </a>
                        </div>
                      )}
                      
                      {distributor.Email && (
                        <div className="flex items-start">
                          <FaEnvelope className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                          <a href={`mailto:${distributor.Email}`} className="text-[#0055B8] hover:underline">
                            {distributor.Email}
                          </a>
                        </div>
                      )}
                      
                      {distributor.Website && (
                        <div className="flex items-start">
                          <FaGlobe className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                          <a 
                            href={distributor.Website.startsWith('http') ? distributor.Website : `https://${distributor.Website}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[#0055B8] hover:underline"
                          >
                            {distributor.Website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-6">
                      <a 
                        href={`https://wa.me/${distributor.Phone ? distributor.Phone.replace(/[^\d+]/g, '') : '525539908312'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-[#25D366] text-white rounded-md hover:bg-[#128C7E] transition-colors"
                      >
                        <FaWhatsapp className="mr-2" /> Contactar por WhatsApp
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Preguntas Frecuentes</h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-2">¿Cuál es la diferencia entre comprar directamente o con un distribuidor?</h3>
              <p className="text-gray-600">
                Ambos canales ofrecen el mismo producto y soporte. La compra directa con ECOSOFT puede ser más rápida para procesos corporativos, mientras que los distribuidores ofrecen atención local y pueden incluir servicios adicionales.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-2">¿Puedo adquirir licencias adicionales con un distribuidor diferente?</h3>
              <p className="text-gray-600">
                Sí, puedes adquirir licencias adicionales con cualquier distribuidor autorizado o directamente con ECOSOFT, independientemente de dónde hayas comprado anteriormente.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-2">¿Los precios varían entre distribuidores?</h3>
              <p className="text-gray-600">
                Los precios de lista son los mismos, pero los distribuidores pueden ofrecer paquetes o promociones especiales. Te recomendamos consultar las opciones disponibles.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-2">¿Qué formas de pago aceptan?</h3>
              <p className="text-gray-600">
                Aceptamos transferencias bancarias, tarjetas de crédito y débito, así como opciones de financiamiento. Consulta con nuestro equipo de ventas o con tu distribuidor local para conocer todas las opciones disponibles.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Distribuidores; 